import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  FolderOpenIcon,
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { agentsAPI } from '../services/api';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';

const WorkflowManagementPage = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const response = await agentsAPI.getWorkflows();
      setWorkflows(response.data?.workflows || []);
    } catch (error) {
      console.error('Error loading workflows:', error);
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async (workflowId) => {
    if (!editName.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }

    try {
      await agentsAPI.updateWorkflow(workflowId, { name: editName.trim() });
      toast.success('Workflow renamed successfully');
      setEditingId(null);
      loadWorkflows();
    } catch (error) {
      console.error('Error renaming workflow:', error);
      toast.error('Failed to rename workflow');
    }
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;
    try {
      await agentsAPI.deleteWorkflow(workflowToDelete.id);
      toast.success('Workflow deleted successfully');
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
      loadWorkflows();
    } catch (error) {
      console.error('Error deleting workflow:', error);
      toast.error('Failed to delete workflow');
    }
  };

  const handleEdit = (workflowId) => {
    navigate(`/workflow-studio?edit=${workflowId}`);
  };

  const handleDuplicate = async (workflow) => {
    try {
      const newWorkflow = {
        ...workflow,
        name: `${workflow.name} (Copy)`,
        workflow_id: `${workflow.workflow_id}_copy_${Date.now()}`
      };
      delete newWorkflow.id;
      await agentsAPI.createWorkflow(newWorkflow);
      toast.success('Workflow duplicated successfully');
      loadWorkflows();
    } catch (error) {
      console.error('Error duplicating workflow:', error);
      toast.error('Failed to duplicate workflow');
    }
  };

  const startEditing = (workflow) => {
    setEditingId(workflow.id);
    setEditName(workflow.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
  };

  const filteredWorkflows = workflows.filter(w =>
    w.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-4">
            <FolderOpenIcon className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Workflow Management</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Manage, edit, rename, and delete your workflows</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={loadWorkflows}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/workflow-studio')}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Workflow
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-6 py-4">
        <div className="relative sm:max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search workflows..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      {/* Workflow List */}
      <div className="px-6 pb-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredWorkflows.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <FolderOpenIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
              {searchTerm ? 'No workflows found' : 'No workflows yet'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm ? 'Try adjusting your search' : 'Create your first workflow to get started'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate('/workflow-studio')}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Create Workflow
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredWorkflows.map((workflow) => (
              <div
                key={workflow.id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    {editingId === workflow.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(workflow.id);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                          className="flex-1 px-3 py-1 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                          autoFocus
                        />
                        <button
                          onClick={() => handleRename(workflow.id)}
                          className="p-1 text-green-600 hover:text-green-700"
                          title="Save"
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Cancel"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                          {workflow.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                          {workflow.description || 'No description'}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                          <span>{workflow.stages_count || Object.keys(workflow.stages || {}).length} stages</span>
                          <span>•</span>
                          <span>{workflow.active ? '✅ Active' : '⏸️ Inactive'}</span>
                          {workflow.metadata?.updated_at && (
                            <>
                              <span>•</span>
                              <span>Updated: {new Date(workflow.metadata.updated_at).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {editingId !== workflow.id && (
                    <div className="flex items-center space-x-2 ml-4">
                        <>
                          <button
                            onClick={() => handleEdit(workflow.id)}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                            title="Edit in Studio"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => startEditing(workflow)}
                            className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                            title="Rename"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDuplicate(workflow)}
                            className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title="Duplicate"
                          >
                            <DocumentDuplicateIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => { setWorkflowToDelete(workflow); setDeleteDialogOpen(true); }}
                            className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setWorkflowToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Workflow"
        entityName={workflowToDelete?.name || ''}
        warningText="This will permanently delete this workflow and all its stages. This action cannot be undone."
        confirmButtonText="Delete Workflow"
      />
    </div>
  );
};

export default WorkflowManagementPage;
